import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Droplet, Zap, Flame, Wrench, Hammer, Home, Refrigerator, Bug,
  MapPin, Clock, Shield, Star, ArrowRight, Check, ChevronRight,
  Camera, X, Loader2, ChevronDown, AlertCircle, User, Menu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Icon mapping for trades
const tradeIcons = {
  'droplet': Droplet,
  'zap': Zap,
  'flame': Flame,
  'wrench': Wrench,
  'hammer': Hammer,
  'home': Home,
  'refrigerator': Refrigerator,
  'bug': Bug,
};

const LandingPage = () => {
  const navigate = useNavigate();
  
  // Form state
  const [step, setStep] = useState(1);
  const [trades, setTrades] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [description, setDescription] = useState('');
  const [addressData, setAddressData] = useState(null); // Full address object with lat/lng
  const [urgency, setUrgency] = useState('today');
  const [photos, setPhotos] = useState([]);
  
  // Quote state
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [showQuoteResult, setShowQuoteResult] = useState(false);
  
  // Mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchTrades();
    
    // Check for pending quote in sessionStorage
    const pendingQuote = sessionStorage.getItem('pendingQuote');
    if (pendingQuote) {
      const parsed = JSON.parse(pendingQuote);
      setQuote(parsed);
      setShowQuoteResult(true);
      setStep(4);
    }
  }, []);

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${API_URL}/public/trades`);
      setTrades(response.data.trades);
    } catch (error) {
      // Use default trades if API fails
      setTrades([
        { id: 'plumbing', name: 'Plumbing', icon: 'droplet', subcategories: ['Leaking tap', 'Blocked drain', 'Toilet issues', 'Burst pipe', 'Other'] },
        { id: 'electrical', name: 'Electrical', icon: 'zap', subcategories: ['Socket issue', 'Light fixture', 'Fuse box', 'Other'] },
        { id: 'heating', name: 'Heating', icon: 'flame', subcategories: ['Boiler repair', 'Radiator issue', 'Thermostat', 'Other'] },
        { id: 'handyman', name: 'Handyman', icon: 'wrench', subcategories: ['Furniture assembly', 'Door/window repair', 'Painting', 'Other'] },
      ]);
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'GBP': '£', 'USD': '$', 'EUR': '€' };
    return symbols[currency] || currency;
  };

  const handleGetQuote = async () => {
    // Validate address has lat/lng
    if (!selectedTrade || !description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!addressData || !addressData.lat || !addressData.lng) {
      toast.error('Please select a valid address from the suggestions');
      return;
    }

    setLoading(true);
    
    try {
      // Use v2 pricing endpoint for new quotes
      const response = await axios.post(`${API_URL}/public/quote/v2`, {
        category: selectedTrade.name,
        subcategory: selectedSubcategory || null,
        description: description,
        urgency: urgency,
        address_text: addressData.formattedAddress,
        latitude: addressData.lat,
        longitude: addressData.lng,
        place_id: addressData.placeId || null,
        postcode: addressData.postcode,
        city: addressData.city,
        country: addressData.countryCode || addressData.country || 'GB',
        photos: photos
      });

      setQuote(response.data);
      setShowQuoteResult(true);
      setStep(4);
      
      // Store quote in sessionStorage for persistence through auth
      sessionStorage.setItem('pendingQuote', JSON.stringify(response.data));
      sessionStorage.setItem('quoteFormData', JSON.stringify({
        selectedTrade,
        selectedSubcategory,
        description,
        addressData,
        urgency,
        photos
      }));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get quote');
    } finally {
      setLoading(false);
    }
  };

  // Handle address selection from autocomplete
  const handleAddressSelected = (address) => {
    setAddressData(address);
  };

  const handleConfirmBooking = () => {
    // Navigate to auth with return URL
    navigate('/auth?mode=login&returnTo=confirm-quote');
  };

  const TradeCard = ({ trade, selected, onClick }) => {
    const Icon = tradeIcons[trade.icon] || Wrench;
    return (
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className={`p-6 rounded-2xl border-2 transition-all text-left ${
          selected 
            ? 'border-black bg-black text-white' 
            : 'border-neutral-200 bg-white hover:border-neutral-400'
        }`}
        data-testid={`trade-${trade.id}`}
      >
        <Icon className={`w-8 h-8 mb-3 ${selected ? 'text-white' : 'text-black'}`} />
        <h3 className="font-semibold text-lg">{trade.name}</h3>
      </motion.button>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <a href="/" className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>ZOLT</a>
              <div className="hidden md:flex items-center gap-6">
                <a href="#how-it-works" className="text-neutral-600 hover:text-black text-sm font-medium">How it works</a>
                <a href="#pricing" className="text-neutral-600 hover:text-black text-sm font-medium">Pricing</a>
                <a href="/become-contractor" className="text-neutral-600 hover:text-black text-sm font-medium">Become a Pro</a>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth?mode=login')}
                className="text-neutral-700"
              >
                Log in
              </Button>
              <Button 
                onClick={() => navigate('/auth?mode=signup')}
                className="bg-black hover:bg-neutral-800 text-white rounded-full px-6"
              >
                Sign up
              </Button>
            </div>
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-neutral-100"
            >
              <div className="px-4 py-4 space-y-3">
                <a href="#how-it-works" className="block py-2 text-neutral-600">How it works</a>
                <a href="#pricing" className="block py-2 text-neutral-600">Pricing</a>
                <a href="/auth?mode=contractor" className="block py-2 text-neutral-600">Become a contractor</a>
                <hr className="border-neutral-200" />
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/auth?mode=login')}
                  className="w-full"
                >
                  Log in
                </Button>
                <Button 
                  onClick={() => navigate('/auth?mode=signup')}
                  className="w-full bg-black text-white"
                >
                  Sign up
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section with Quote Form */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Left: Quote Form */}
            <div className="order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl shadow-2xl shadow-black/5 border border-neutral-100 p-8"
              >
                {/* Progress Steps */}
                <div className="flex items-center gap-2 mb-8">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                        step >= s ? 'bg-black text-white' : 'bg-neutral-100 text-neutral-400'
                      }`}>
                        {step > s ? <Check className="w-4 h-4" /> : s}
                      </div>
                      {s < 3 && <div className={`w-12 h-0.5 mx-2 ${step > s ? 'bg-black' : 'bg-neutral-200'}`} />}
                    </div>
                  ))}
                </div>

                {/* Step 1: Select Trade */}
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-6"
                  >
                    <div>
                      <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        What needs fixing?
                      </h2>
                      <p className="text-neutral-500">Select the type of service you need</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {trades.map((trade) => (
                        <TradeCard
                          key={trade.id}
                          trade={trade}
                          selected={selectedTrade?.id === trade.id}
                          onClick={() => {
                            setSelectedTrade(trade);
                            setSelectedSubcategory('');
                          }}
                        />
                      ))}
                    </div>

                    {selectedTrade && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="space-y-3"
                      >
                        <label className="text-sm font-medium text-neutral-700">What's the issue?</label>
                        <div className="flex flex-wrap gap-2">
                          {selectedTrade.subcategories.map((sub) => (
                            <button
                              key={sub}
                              onClick={() => setSelectedSubcategory(sub)}
                              className={`px-4 py-2 rounded-full text-sm transition-all ${
                                selectedSubcategory === sub
                                  ? 'bg-black text-white'
                                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                              }`}
                            >
                              {sub}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}

                    <Button
                      onClick={() => setStep(2)}
                      disabled={!selectedTrade}
                      className="w-full h-14 bg-black hover:bg-neutral-800 text-white rounded-xl text-lg"
                      data-testid="step1-next"
                    >
                      Continue <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </motion.div>
                )}

                {/* Step 2: Details */}
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-6"
                  >
                    <div>
                      <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Tell us more
                      </h2>
                      <p className="text-neutral-500">Describe the issue and where it is</p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-neutral-700 mb-2 block">
                          Describe the problem
                        </label>
                        <Textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="E.g., The kitchen tap has been dripping for a few days..."
                          className="min-h-[100px] resize-none"
                          data-testid="description-input"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-neutral-700 mb-2 block">
                          Address
                        </label>
                        <AddressAutocompleteInput
                          value={addressData?.formattedAddress || ''}
                          onAddressSelected={handleAddressSelected}
                          placeholder="Enter your address (worldwide)"
                          countryRestriction={null}
                          showRecentAddresses={false}
                          showSavedAddresses={false}
                          testId="landing-address"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-neutral-700 mb-2 block">
                          When do you need this done?
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { id: 'now', label: 'Now', desc: 'ASAP' },
                            { id: 'today', label: 'Today', desc: '2-4 hrs' },
                            { id: 'scheduled', label: 'Schedule', desc: 'Pick date' }
                          ].map((opt) => (
                            <button
                              key={opt.id}
                              onClick={() => setUrgency(opt.id)}
                              className={`p-4 rounded-xl border-2 text-center transition-all ${
                                urgency === opt.id
                                  ? 'border-black bg-black text-white'
                                  : 'border-neutral-200 hover:border-neutral-400'
                              }`}
                              data-testid={`urgency-${opt.id}`}
                            >
                              <div className="font-semibold">{opt.label}</div>
                              <div className={`text-xs ${urgency === opt.id ? 'text-neutral-300' : 'text-neutral-500'}`}>
                                {opt.desc}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setStep(1)}
                        className="flex-1 h-14 rounded-xl"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={() => setStep(3)}
                        disabled={!description || !addressData?.lat}
                        className="flex-1 h-14 bg-black hover:bg-neutral-800 text-white rounded-xl"
                        data-testid="step2-next"
                      >
                        Continue
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Review & Get Quote */}
                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-6"
                  >
                    <div>
                      <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Get your price
                      </h2>
                      <p className="text-neutral-500">Review your request and get an instant quote</p>
                    </div>

                    <div className="bg-neutral-50 rounded-2xl p-6 space-y-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm text-neutral-500">Service</div>
                          <div className="font-semibold">{selectedTrade?.name}</div>
                          {selectedSubcategory && (
                            <div className="text-sm text-neutral-600">{selectedSubcategory}</div>
                          )}
                        </div>
                        <button
                          onClick={() => setStep(1)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Edit
                        </button>
                      </div>
                      
                      <hr className="border-neutral-200" />
                      
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm text-neutral-500">Description</div>
                          <div className="text-sm">{description}</div>
                        </div>
                        <button
                          onClick={() => setStep(2)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Edit
                        </button>
                      </div>
                      
                      <hr className="border-neutral-200" />
                      
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm text-neutral-500">Location</div>
                          <div className="text-sm flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {addressData?.formattedAddress || 'No address selected'}
                          </div>
                          {addressData?.postcode && (
                            <div className="text-xs text-neutral-400 mt-1">
                              {addressData.postcode}, {addressData.city}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => setStep(2)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Edit
                        </button>
                      </div>
                      
                      <hr className="border-neutral-200" />
                      
                      <div>
                        <div className="text-sm text-neutral-500">Urgency</div>
                        <div className="text-sm font-medium capitalize">
                          {urgency === 'now' ? 'Immediate (ASAP)' : urgency === 'today' ? 'Today (2-4 hours)' : 'Scheduled'}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setStep(2)}
                        className="flex-1 h-14 rounded-xl"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handleGetQuote}
                        disabled={loading}
                        className="flex-1 h-14 bg-black hover:bg-neutral-800 text-white rounded-xl text-lg"
                        data-testid="get-quote-btn"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Getting quote...
                          </>
                        ) : (
                          <>Get Price</>
                        )}
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Quote Result */}
                {step === 4 && quote && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-6"
                  >
                    <div className="text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-600" />
                      </div>
                      <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Your Quote
                      </h2>
                      <p className="text-neutral-500">Transparent pricing, no hidden fees</p>
                    </div>

                    {/* Price Breakdown - V2: Diagnostic Fee + Estimate Ranges */}
                    <div className="bg-neutral-50 rounded-2xl p-6 space-y-4">
                      {/* Diagnostic Fee (charged now) */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-semibold text-amber-900">
                              {quote.diagnostic_fee_label || "On-Site Diagnostic & Assessment Fee"}
                            </span>
                            <p className="text-xs text-amber-700 mt-1">Charged at booking to dispatch contractor</p>
                          </div>
                          <span className="font-bold text-amber-900 text-lg">
                            {getCurrencySymbol(quote.currency)}{(quote.diagnostic_fee_amount || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Estimate Ranges */}
                      <div className="space-y-3 pt-2">
                        <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Estimate Ranges (confirmed after on-site assessment)</p>
                        
                        {/* Labour Range */}
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-600">
                            Labour estimate range
                          </span>
                          <span className="font-medium">
                            {getCurrencySymbol(quote.currency)}{(quote.estimate_labour_low || quote.labor_estimate || 0).toFixed(0)}–{(quote.estimate_labour_high || quote.labor_estimate || 0).toFixed(0)}
                          </span>
                        </div>
                        
                        {/* Materials Range */}
                        {(quote.estimate_materials_high > 0 || quote.materials_estimate > 0) && (
                          <div className="flex justify-between text-sm">
                            <span className="text-neutral-600">Materials estimate range</span>
                            <span className="font-medium">
                              {getCurrencySymbol(quote.currency)}{(quote.estimate_materials_low || 0).toFixed(0)}–{(quote.estimate_materials_high || quote.materials_estimate || 0).toFixed(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <hr className="border-neutral-200" />
                      
                      {/* Total Range */}
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-lg font-bold">Estimated Total Range</span>
                          <p className="text-xs text-neutral-500">Final quote confirmed after on-site assessment</p>
                        </div>
                        <span className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {getCurrencySymbol(quote.currency)}{(quote.estimate_total_low || quote.total_estimate || 0).toFixed(0)}–{(quote.estimate_total_high || quote.total_estimate || 0).toFixed(0)}
                        </span>
                      </div>
                      
                      {/* Platform Fee Info */}
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                        <div className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <span className="text-green-700 font-medium">
                              No extra platform fees — ZOLT is funded from the Diagnostic Fee (6%)
                            </span>
                            <p className="text-green-600 text-xs mt-1">
                              You approve the final quote before any work begins
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Estimate Confidence */}
                    <div className="flex items-center justify-between text-sm text-neutral-600">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          quote.ai_confidence >= 0.7 ? 'bg-green-500' : 
                          quote.ai_confidence >= 0.5 ? 'bg-yellow-500' : 'bg-orange-500'
                        }`} />
                        <span>Estimate confidence: {Math.round(quote.ai_confidence * 100)}%</span>
                      </div>
                      <a href="/how-pricing-works" className="text-blue-600 hover:underline text-xs">
                        How pricing works
                      </a>
                    </div>

                    {/* What happens next */}
                    <div className="bg-blue-50 rounded-xl p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">What happens next?</h4>
                      <ol className="text-sm text-blue-800 space-y-1">
                        <li>1. Sign in & pay the Diagnostic Fee to dispatch a contractor</li>
                        <li>2. Verified contractor arrives & assesses the job on-site</li>
                        <li>3. You&apos;ll receive a final quote to approve</li>
                        <li>4. Work begins only after your approval</li>
                      </ol>
                    </div>

                    {/* Trust badges */}
                    <div className="grid grid-cols-3 gap-4 text-center text-xs">
                      <div>
                        <Shield className="w-5 h-5 mx-auto mb-1 text-neutral-400" />
                        <span className="text-neutral-500">Verified Pros</span>
                      </div>
                      <div>
                        <Check className="w-5 h-5 mx-auto mb-1 text-neutral-400" />
                        <span className="text-neutral-500">You approve quote</span>
                      </div>
                      <div>
                        <Clock className="w-5 h-5 mx-auto mb-1 text-neutral-400" />
                        <span className="text-neutral-500">60 min validity</span>
                      </div>
                    </div>


                    <Button
                      onClick={handleConfirmBooking}
                      className="w-full h-14 bg-black hover:bg-neutral-800 text-white rounded-xl text-lg"
                      data-testid="confirm-booking-btn"
                    >
                      Sign in to Confirm Booking <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>

                    <button
                      onClick={() => {
                        setStep(1);
                        setQuote(null);
                        setShowQuoteResult(false);
                        setAddressData(null);
                        setDescription('');
                        setSelectedTrade(null);
                        setSelectedSubcategory('');
                        sessionStorage.removeItem('pendingQuote');
                        sessionStorage.removeItem('quoteFormData');
                      }}
                      className="w-full text-center text-sm text-neutral-500 hover:text-neutral-700"
                    >
                      Start new quote
                    </button>
                  </motion.div>
                )}
              </motion.div>
            </div>

            {/* Right: Hero Content */}
            <div className="order-1 lg:order-2 lg:sticky lg:top-32">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Fix anything.
                  <br />
                  <span className="text-neutral-400">Anytime.</span>
                </h1>
                <p className="text-xl text-neutral-600 mb-4 max-w-lg">
                  Get instant estimate ranges and book a verified contractor in minutes. 
                  Transparent pricing, no hidden fees, real-time tracking.
                </p>
                
                {/* Pricing trust line */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-6 max-w-lg">
                  <p className="text-sm text-green-800">
                    <span className="font-semibold">How it works:</span> Pay a Diagnostic Fee to dispatch a contractor. 
                    Get estimate ranges upfront. Approve the final quote after on-site assessment.
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-6 mb-8">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="text-neutral-700">Estimate ranges upfront</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="text-neutral-700">Verified contractors</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="text-neutral-700">Approve before work starts</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-6 p-6 bg-neutral-50 rounded-2xl">
                  <div>
                    <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>15k+</div>
                    <div className="text-sm text-neutral-500">Jobs completed</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>4.9</div>
                    <div className="text-sm text-neutral-500">Avg. rating</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>&lt;30m</div>
                    <div className="text-sm text-neutral-500">Avg. response</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="py-24 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How ZOLT works
            </h2>
            <p className="text-neutral-600 text-lg">Simple, transparent, you&apos;re always in control</p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {[
              { step: '01', title: 'Tell us the issue', desc: 'Describe what needs fixing and where', icon: '💬' },
              { step: '02', title: 'See estimate ranges', desc: 'Get labour + materials estimates instantly', icon: '📊' },
              { step: '03', title: 'Pay Diagnostic Fee', desc: 'Dispatch a verified contractor to your location', icon: '💳' },
              { step: '04', title: 'On-site assessment', desc: 'Contractor diagnoses and confirms final quote', icon: '🔍' },
              { step: '05', title: 'You decide', desc: 'Accept or decline. Work starts after your approval', icon: '✅' },
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl mb-3">{item.icon}</div>
                <div className="text-xs font-medium text-neutral-400 mb-1">{item.step}</div>
                <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Transparent pricing
          </h2>
          <p className="text-neutral-600 text-lg mb-12">
            No hidden fees. You approve the final quote before any work begins.
          </p>

          <div className="bg-neutral-50 rounded-3xl p-8 md:p-12 text-left">
            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-center py-3 border-b border-neutral-200">
                <div>
                  <span className="text-neutral-700 font-medium">Diagnostic Fee</span>
                  <p className="text-sm text-neutral-500">Charged at booking to dispatch a contractor</p>
                </div>
                <span className="font-semibold text-amber-600">Paid upfront</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-neutral-200">
                <div>
                  <span className="text-neutral-700 font-medium">Labour estimate range</span>
                  <p className="text-sm text-neutral-500">Smart estimate based on job details</p>
                </div>
                <span className="font-medium text-neutral-600">Shown upfront</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-neutral-200">
                <div>
                  <span className="text-neutral-700 font-medium">Materials estimate range</span>
                  <p className="text-sm text-neutral-500">Only if parts or supplies are needed</p>
                </div>
                <span className="font-medium text-neutral-600">Shown upfront</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <div>
                  <span className="text-neutral-700 font-medium">Platform fee for customers</span>
                  <p className="text-sm text-neutral-500">ZOLT is funded from the Diagnostic Fee (6%)</p>
                </div>
                <span className="font-bold text-green-600">£0 extra</span>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-white/90 mb-1 text-sm">You&apos;re always in control</p>
                  <p className="text-xl font-bold">Final quote confirmed after on-site assessment</p>
                  <p className="text-white/80 mt-1 text-sm">No extra platform fees — ZOLT is funded from the Diagnostic Fee</p>
                </div>
                <a href="/how-pricing-works" className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap">
                  Learn more <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Safety Section */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built on trust
            </h2>
            <p className="text-neutral-600 text-lg">Fair, transparent pricing that protects you</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: <Shield className="w-6 h-6" />, 
                title: 'Estimate ranges upfront', 
                desc: 'See labour and materials ranges before you dispatch a contractor'
              },
              { 
                icon: <Check className="w-6 h-6" />, 
                title: 'Final quote approval', 
                desc: 'Work only starts after you review and approve the on-site quote'
              },
              { 
                icon: <AlertCircle className="w-6 h-6" />, 
                title: 'Over-range transparency', 
                desc: 'If a quote exceeds the estimate range, reasons are shown to you'
              },
              { 
                icon: <Star className="w-6 h-6" />, 
                title: 'Verified contractors', 
                desc: 'Background-checked professionals with documented pricing'
              },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-neutral-200"
              >
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mb-4">
                  {item.icon}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to fix it?
          </h2>
          <p className="text-xl text-white/70 mb-8">
            Get instant estimate ranges now. Approve the final quote after on-site assessment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setStep(1);
              }}
              className="bg-white text-black hover:bg-neutral-200 rounded-full px-12 py-6 text-lg font-medium"
            >
              Get a Quote
            </Button>
            <a href="/how-pricing-works">
              <Button 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium"
              >
                See Pricing
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>ZOLT</div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-neutral-600">
              <a href="#how-it-works" className="hover:text-black">How it works</a>
              <a href="#pricing" className="hover:text-black">Pricing</a>
              <a href="/become-contractor" className="hover:text-black font-medium">Become a Pro</a>
              <a href="#" className="hover:text-black">Privacy</a>
              <a href="#" className="hover:text-black">Terms</a>
              <a href="#" className="hover:text-black">Contact</a>
            </div>
            <p className="text-sm text-neutral-500">© 2026 ZOLT X Limited</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
